import type { ReactNode } from "react";
import { Link } from "wouter";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CalendarCheck,
  CalendarDays,
  CalendarPlus,
  Check,
  ChevronDown,
  EyeOff,
  RefreshCw,
  Repeat,
  Settings,
  X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge, DarkPill, IconTile, OutlinePill } from "@/components/calendar/ui";
import { DURATIONS, type CalendarConnection, type Frequency, type TimeOfDay } from "@/lib/calendar";
import { cn } from "@/lib/utils";

/** White column card used by all three Find a time columns. */
export function PlanCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-[24px] border border-foreground/[0.05] bg-white shadow-[0_1px_2px_rgba(47,41,37,0.04)]", className)}>
      {children}
    </div>
  );
}

export function SegmentedControl<T extends string | number>({
  options,
  value,
  onChange,
  label,
  className,
  itemClassName,
  disabled,
}: {
  options: { value: T; label: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
  className?: string;
  itemClassName?: string;
  disabled?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label={label} className={cn("flex rounded-[14px] bg-[#f4f1ec] p-1", className)}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(o.value)}
            className={cn(
              "h-10 flex-1 rounded-[11px] text-[14px] tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed",
              active
                ? "bg-white font-medium text-foreground shadow-[0_1px_1.5px_rgba(47,41,37,0.12)]"
                : "text-muted-foreground hover:text-foreground disabled:hover:text-muted-foreground",
              itemClassName,
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Top bar ──────────────────────────────────────────────────────────────────

export function PlanTopBar({
  title,
  connection,
  calendarId,
  onCalendarChange,
  onClose,
  locked = false,
}: {
  title: string;
  connection: CalendarConnection | null;
  calendarId: string | undefined;
  onCalendarChange: (id: string) => void;
  onClose: () => void;
  /** Rescheduling moves the event where it is, so its calendar can't change. */
  locked?: boolean;
}) {
  const ended = connection?.status === "needs_reconnect";
  const calendar = connection?.calendars.find((c) => c.id === calendarId);

  return (
    <header className="sticky top-0 z-40 border-b border-foreground/[0.07] bg-[#f8f6f2]/95 backdrop-blur">
      <div className="flex h-[61px] items-center gap-3 px-4 sm:px-8">
        <button
          type="button"
          onClick={onClose}
          className="-ml-2 flex items-center gap-2 rounded-lg px-2 py-1.5 text-[15px] text-foreground/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <ArrowLeft className="h-4 w-4" />
          Momentum
        </button>
        <span aria-hidden className="hidden h-6 w-px bg-foreground/10 sm:block" />
        <h1 className="hidden text-[17px] font-semibold text-foreground sm:block">{title}</h1>

        <div className="ml-auto flex min-w-0 items-center gap-2">
          {connection && locked && (
            <span className="flex min-w-0 items-center gap-2 rounded-full border border-foreground/10 bg-white px-3.5 py-2 text-[13px] text-foreground">
              <CalendarDays className="h-4 w-4 shrink-0 text-primary" />
              <span className="truncate">Google Calendar · {calendar?.name ?? connection.account.email}</span>
            </span>
          )}
          {connection && !locked && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "flex min-w-0 items-center gap-2 rounded-full border bg-white px-3.5 py-2 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                    ended ? "border-[#e2a4aa] text-[#a8323e]" : "border-foreground/10 text-foreground hover:border-foreground/20",
                  )}
                >
                  <CalendarDays className={cn("h-4 w-4 shrink-0", ended ? "text-[#a8323e]" : "text-primary")} />
                  <span className="truncate">
                    Google Calendar · {ended ? "needs reconnecting" : (calendar?.name ?? connection.account.email)}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={6}
                className="z-[70] w-[280px] rounded-2xl border border-foreground/[0.06] bg-white p-1.5 text-foreground shadow-[0_16px_40px_-12px_rgba(47,41,37,0.28)]"
              >
                <DropdownMenuLabel className="px-3 pb-1 pt-2 text-xs font-normal text-muted-foreground">
                  Add to · {connection.account.email}
                </DropdownMenuLabel>
                <DropdownMenuRadioGroup value={calendarId} onValueChange={onCalendarChange}>
                  {connection.calendars.map((c) => (
                    <DropdownMenuRadioItem
                      key={c.id}
                      value={c.id}
                      className="cursor-pointer rounded-xl py-2.5 pl-8 pr-3 text-[14px] focus:bg-foreground/[0.04] data-[highlighted]:bg-foreground/[0.04]"
                    >
                      {c.name}
                      {c.primary && <span className="ml-1.5 text-xs text-muted-foreground">primary</span>}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator className="mx-1 bg-foreground/[0.06]" />
                <DropdownMenuItem asChild className="cursor-pointer rounded-xl px-3 py-2.5 text-[14px] focus:bg-foreground/[0.04]">
                  <Link href="/settings#calendar">
                    <Settings className="mr-2 h-4 w-4 text-foreground/60" />
                    Calendar settings
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-foreground/60 hover:bg-foreground/[0.05] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </header>
  );
}

// ── Left column ──────────────────────────────────────────────────────────────

const FREQUENCIES: { value: Frequency; label: string; hint: string }[] = [
  { value: "once", label: "Once", hint: "One event" },
  { value: "daily", label: "Daily", hint: "Every day, or weekdays" },
  { value: "weekly", label: "Weekly", hint: "1–3 times a week" },
  { value: "biweekly", label: "Every 2 weeks", hint: "A check-in rhythm" },
];

const PARTS: { value: TimeOfDay; label: string }[] = [
  { value: "morning", label: "Morning" },
  { value: "afternoon", label: "Afternoon" },
  { value: "evening", label: "Evening" },
  { value: "any", label: "Any" },
];

export function StepPanel({
  stepText,
  journalTitle,
  durationMin,
  onDuration,
  frequency,
  onFrequency,
  frequencyHint,
  lockFrequency,
  prefer,
  onPrefer,
  freeBusy,
}: {
  stepText: string;
  journalTitle: string;
  durationMin: number;
  onDuration: (m: number) => void;
  frequency: Frequency;
  onFrequency: (f: Frequency) => void;
  /** Overrides the selected option's hint, e.g. "2 times a week". */
  frequencyHint?: string;
  /** Rescheduling moves one event, so the rhythm options are off. */
  lockFrequency: "once" | "rhythm" | null;
  prefer: TimeOfDay;
  onPrefer: (p: TimeOfDay) => void;
  freeBusy: boolean;
}) {
  return (
    <PlanCard className="flex h-full flex-col p-6">
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-primary">Next step</p>
      <p className="mt-2 font-display text-[21px] font-semibold leading-[1.25] tracking-[-0.01em] text-foreground">{stepText}</p>
      <p className="mt-2 text-[12.5px] text-muted-foreground">
        From <span className="text-foreground/80">{journalTitle}</span>
      </p>

      <div className="my-5 h-px bg-foreground/[0.07]" />

      <div className="flex items-baseline justify-between">
        <p className="text-[14px] font-medium text-foreground">How long</p>
        <p className="text-xs text-muted-foreground">minutes</p>
      </div>
      <SegmentedControl
        className="mt-2.5"
        label="How long, in minutes"
        options={DURATIONS.map((d) => ({ value: d, label: d }))}
        value={durationMin}
        onChange={onDuration}
      />

      <p className="mt-6 text-[14px] font-medium text-foreground">How often</p>
      <div role="radiogroup" aria-label="How often" className="mt-2.5 space-y-2">
        {FREQUENCIES.map((f) => {
          const active = f.value === frequency;
          const locked = lockFrequency === "once" ? f.value !== "once" : lockFrequency === "rhythm" ? f.value === "once" : false;
          return (
            <button
              key={f.value}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={locked}
              onClick={() => onFrequency(f.value)}
              className={cn(
                "flex h-[42px] w-full items-center gap-3 rounded-[12px] border px-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-45",
                active
                  ? "border-primary/50 bg-primary/[0.05] shadow-[0_0_0_3px_rgba(110,83,172,0.06)]"
                  : "border-foreground/[0.1] hover:border-foreground/20",
              )}
            >
              <span
                className={cn(
                  "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-[1.5px]",
                  active ? "border-primary" : "border-foreground/35",
                )}
              >
                {active && <span className="h-2 w-2 rounded-full bg-primary" />}
              </span>
              <span className={cn("flex-1 text-[14.5px]", active ? "font-medium text-primary" : "text-foreground")}>{f.label}</span>
              <span className="text-xs text-muted-foreground">{active && frequencyHint ? frequencyHint : f.hint}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-6 flex items-baseline justify-between">
        <p className="text-[14px] font-medium text-foreground">Best time of day</p>
        <p className="text-xs text-muted-foreground">Shapes suggestions</p>
      </div>
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {PARTS.map((p) => (
          <button
            key={p.value}
            type="button"
            aria-pressed={p.value === prefer}
            onClick={() => onPrefer(p.value)}
            className={cn(
              "h-8 rounded-full border px-3 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
              p.value === prefer
                ? "border-foreground bg-foreground text-white"
                : "border-foreground/[0.12] text-foreground hover:border-foreground/25",
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="mt-auto pt-6">
        <p className="flex gap-2.5 rounded-[14px] bg-primary/[0.07] px-3.5 py-3 text-[12.5px] leading-relaxed text-foreground/80">
          <EyeOff className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
          {freeBusy
            ? "UnfoldWIN checks only when you're busy, to suggest free times. It never sees what your events are."
            : "UnfoldWIN isn't checking your calendar, so times aren't matched to your day. It never sees what your events are."}
        </p>
      </div>
    </PlanCard>
  );
}

// ── Footer ───────────────────────────────────────────────────────────────────

export type FooterTone = "ok" | "overlap" | "ended" | "loading" | "second" | "rhythm" | "plain";

export function PlanFooter({
  tone,
  title,
  subtitle,
  badge,
  primaryLabel,
  primaryIcon = "calendar",
  onPrimary,
  primaryDisabled,
  pending,
  onCancel,
  cancelLabel = "Cancel",
}: {
  tone: FooterTone;
  title: string;
  subtitle: string;
  badge?: { tone: "green" | "red" | "amber"; label: string } | null;
  primaryLabel: string;
  primaryIcon?: "calendar" | "arrow" | "reconnect" | "none";
  onPrimary: () => void;
  primaryDisabled?: boolean;
  pending?: boolean;
  onCancel: () => void;
  cancelLabel?: string;
}) {
  const ended = tone === "ended";
  const warn = tone === "overlap" || ended;
  return (
    <footer
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 border-t pb-[env(safe-area-inset-bottom)]",
        ended ? "border-[#f0c9cc] bg-[#fdf0f0]" : "border-foreground/[0.07] bg-white",
      )}
    >
      <div className="flex flex-col gap-3 px-4 py-3 sm:px-8 md:h-[72px] md:flex-row md:items-center md:gap-4 md:py-0">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <IconTile tone={warn ? "red" : "violet"} className="hidden sm:flex">
            {warn ? (
              <AlertTriangle className="h-[18px] w-[18px]" />
            ) : tone === "rhythm" ? (
              <Repeat className="h-[18px] w-[18px]" />
            ) : (
              <CalendarCheck className="h-[18px] w-[18px]" />
            )}
          </IconTile>
          <div className="min-w-0">
            <p className={cn("truncate text-[15px] font-semibold", ended ? "text-[#8f2a33]" : "text-foreground")}>{title}</p>
            <p className="truncate text-[12.5px] text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5 md:gap-3">
          {badge && (
            <Badge
              tone={badge.tone}
              className="hidden lg:inline-flex"
              icon={badge.tone === "green" ? <Check className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            >
              {badge.label}
            </Badge>
          )}
          <OutlinePill className="flex-1 md:flex-none" onClick={onCancel}>
            {cancelLabel}
          </OutlinePill>
          <DarkPill className="flex-[2] md:flex-none" loading={pending} disabled={primaryDisabled} onClick={onPrimary}>
            {!pending && primaryIcon === "calendar" && <CalendarPlus className="h-4 w-4" />}
            {!pending && primaryIcon === "reconnect" && <RefreshCw className="h-4 w-4" />}
            {primaryLabel}
            {!pending && primaryIcon === "arrow" && <ArrowRight className="h-4 w-4" />}
          </DarkPill>
        </div>
      </div>
    </footer>
  );
}

/** A notice at the top of the middle column (already added, moving, changing a rhythm). */
export function PlanNotice({
  tone,
  title,
  children,
}: {
  tone: "amber" | "violet";
  title: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "mb-5 flex gap-3 rounded-2xl px-4 py-3.5",
        tone === "amber" ? "bg-[#fbf1d6] ring-1 ring-inset ring-[#ecd28c]" : "bg-primary/[0.07]",
      )}
    >
      <AlertTriangle className={cn("mt-0.5 h-4 w-4 shrink-0", tone === "amber" ? "text-[#86621a]" : "text-primary")} />
      <div className="min-w-0 text-[13px] leading-relaxed text-foreground/80">
        <p className="text-[14px] font-semibold text-foreground">{title}</p>
        {children}
      </div>
    </div>
  );
}
